import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMoney, parsePaise } from './allocation';
import { mapPaymentError } from '../../api/endpoints/payments';
import {
  useApprovePayment,
  useMarkPaymentPaid,
  useOpenPaymentItems,
  usePayment,
  useRejectPayment,
  useRequestPayout,
  useVoidPayment,
  useVoidPaymentAllocation,
} from './hooks';
import { AllocateReceiptCreditFlow } from './AllocateReceiptCreditFlow';
import type { OrderAdvanceTerms } from './advancePercentage';
import { NewPayoutDialog } from './NewPayoutDialog';
import { VerifyClaimFlow } from './VerifyClaimFlow';
import { PaymentDrawer } from './PaymentDrawer';
import { MarkPaidDialog } from './MarkPaidDialog';
import { CancelPaymentDialog } from './CancelPaymentDialog';
import { VoidAllocationDialog } from './VoidAllocationDialog';
import { ReasonDialog } from './ReasonDialog';
import styles from './Payments.module.css';
import { Button } from '../../components/ui/Button';

type Reason =
  | { kind: 'reject' | 'voidPayment'; id: number; direction?: 'IN' | 'OUT' }
  | { kind: 'voidAllocation'; paymentId: number; allocationId: number };
export function OrderPaymentDialogs({
  shipmentId,
  partnerId,
  partnerAdvancePct,
  advanceTerms,
  payeeName,
  isManager,
  readOnly,
  payout,
  paymentId,
  onClosePayout,
  onClosePayment,
}: {
  shipmentId: number;
  partnerId?: number | null;
  partnerAdvancePct?: number;
  advanceTerms?: OrderAdvanceTerms;
  payeeName?: string;
  readOnly?: boolean;
  isManager: boolean;
  payout: boolean;
  paymentId: number | null;
  onClosePayout: () => void;
  onClosePayment: () => void;
}) {
  const { t } = useTranslation();
  const items = useOpenPaymentItems('OUT', payout ? (partnerId ?? undefined) : undefined);

  const request = useRequestPayout();
  const approve = useApprovePayment();
  const reject = useRejectPayment();
  const voidPayment = useVoidPayment();
  const voidAllocation = useVoidPaymentAllocation();
  const paid = useMarkPaymentPaid();
  const queryClient = useQueryClient();
  const [claimId, setClaimId] = useState<number | null>(null);
  const [creditSuccess, setCreditSuccess] = useState<string | null>(null);
  const [receiptAfterAllocation, setReceiptAfterAllocation] = useState<number | null>(null);
  const detail = usePayment(receiptAfterAllocation ?? paymentId ?? undefined);
  const [creditPaymentId, setCreditPaymentId] = useState<number | null>(null);
  const [markPaidId, setMarkPaidId] = useState<number | null>(null);
  const markDetail = usePayment(markPaidId ?? undefined);
  const [reason, setReason] = useState<Reason | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pending =
    approve.isPending || reject.isPending || voidPayment.isPending || voidAllocation.isPending;
  const run = async (action: () => Promise<unknown>) => {
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(mapPaymentError(cause));
    }
  };
  const reasonAction = (kind: 'reject' | 'voidPayment', id: number) => {
    setError(null);
    if (isManager && !readOnly) setReason({ kind, id, direction: detail.data?.direction });
  };
  const loading = (failed: boolean, cause: unknown, retry: () => unknown, close: () => void) => (
    <div className={styles.scrim}>
      <section
        role="dialog"
        aria-modal="true"
        aria-label={t('payments.orderPaymentDialogs.detailsLabel')}
        className={`${styles.dialog} ${styles.compact}`}
      >
        <div className={styles.body}>
          {failed ? (
            <p role="alert">
              {mapPaymentError(cause)} <button onClick={retry}>{t('payments.orderPaymentDialogs.retry')}</button>
            </p>
          ) : (
            <p role="status">{t('payments.orderPaymentDialogs.loading')}</p>
          )}
          <Button variant="secondary" size="sm" onClick={close}>
            {t('common.actions.cancel')}
          </Button>
        </div>
      </section>
    </div>
  );
  return (
    <>
      {creditSuccess && <p role="status">{creditSuccess}</p>}
      {creditPaymentId != null && (
        <AllocateReceiptCreditFlow
          readOnly={readOnly}
          paymentId={creditPaymentId}
          onAllocated={(fresh, lines) => {
            setCreditPaymentId(null);
            setReceiptAfterAllocation(fresh.id);
            setCreditSuccess(
              `Receipt #${fresh.id}: ${formatMoney(lines.reduce((sum, line) => sum + parsePaise(line.amount), 0n))} cash allocated for ${lines.map((line) => `#${line.shipmentId}`).join(', ')}. Remaining credit: ${formatMoney(parsePaise(fresh.unallocatedCredit))}.`,
            );
          }}
          onClose={() => setCreditPaymentId(null)}
        />
      )}
      {payout &&
        !items.data &&
        loading(items.isError, items.error, () => void items.refetch(), onClosePayout)}
      {payout && items.data && (
        <NewPayoutDialog
          openItems={items.data}
          entryScope="SELECTED"
          initialShipmentId={shipmentId}
          partnerAdvancePct={partnerAdvancePct}
          advanceTerms={advanceTerms}
          submitting={request.isPending}
          onClose={onClosePayout}
          onSubmit={async (body) => {
            const created = await request.mutateAsync({ request: body });
            // Demo (#297): the sheet shows who approves it, then the user closes it.
            if (created?.approval) return created;
            onClosePayout();
          }}
        />
      )}
      {paymentId &&
        !detail.data &&
        loading(detail.isError, detail.error, () => void detail.refetch(), onClosePayment)}
      {(receiptAfterAllocation ?? paymentId) && detail.data && (
        <PaymentDrawer
          readOnly={readOnly}
          payment={detail.data}
          role={isManager ? 'OPS_MANAGER' : 'OPS_EXECUTIVE'}
          onClose={() => {
            setReceiptAfterAllocation(null);
            onClosePayment();
          }}
          actionPending={pending}
          actionError={error}
          onVerifyClaim={(id) => {
            onClosePayment();
            setClaimId(id);
          }}
          onApprove={(id) => void run(() => approve.mutateAsync(id))}
          onReject={(id) => reasonAction('reject', id)}
          onMarkPaid={(id) => {
            setError(null);
            onClosePayment();
            setMarkPaidId(id);
          }}
          onAllocateCredit={(id) => {
            onClosePayment();
            setCreditPaymentId(id);
          }}
          onVoidPayment={(id) => reasonAction('voidPayment', id)}
          onVoidAllocation={(allocationId) => {
            if (!isManager || readOnly) return;
            setError(null);
            setReason({ kind: 'voidAllocation', paymentId: detail.data!.id, allocationId });
          }}
        />
      )}
      {claimId != null && <VerifyClaimFlow paymentId={claimId} onClose={() => setClaimId(null)} />}
      {markPaidId &&
        !markDetail.data &&
        loading(
          markDetail.isError,
          markDetail.error,
          () => void markDetail.refetch(),
          () => setMarkPaidId(null),
        )}
      {markPaidId && markDetail.data && (
        <MarkPaidDialog
          onReconciledPaid={() => queryClient.invalidateQueries({ queryKey: ['payments'] })}
          paymentId={markPaidId}
          payment={markDetail.data}
          payeeName={payeeName}
          submitting={paid.isPending}
          onClose={() => setMarkPaidId(null)}
          onSubmit={async (id, body) => {
            await paid.mutateAsync({ paymentId: id, request: body });
            setMarkPaidId(null);
          }}
        />
      )}
      {reason?.kind === 'voidAllocation' && isManager && !readOnly && (
        <VoidAllocationDialog
          key={`${reason.paymentId}-${reason.allocationId}`}
          paymentId={reason.paymentId}
          allocationId={reason.allocationId}
          submitting={pending}
          error={error}
          onCancel={() => {
            setReason(null);
            setError(null);
          }}
          onSubmit={async (text) => {
            if (!isManager || readOnly || pending) return;
            setError(null);
            try {
              await voidAllocation.mutateAsync({ allocationId: reason.allocationId, reason: text });
              setReason(null);
            } catch (cause) {
              setError(mapPaymentError(cause));
            }
          }}
        />
      )}
      {reason?.kind === 'voidPayment' && isManager && !readOnly && (
        <CancelPaymentDialog
          key={reason.id}
          paymentId={reason.id}
          submitting={pending}
          error={error}
          onClose={() => {
            setReason(null);
            setError(null);
          }}
          onSubmit={async (text) => {
            if (!isManager || readOnly || pending) return;
            setError(null);
            try {
              await voidPayment.mutateAsync({ paymentId: reason.id, reason: text });
              setReason(null);
            } catch (cause) {
              setError(mapPaymentError(cause));
            }
          }}
        />
      )}
      {reason?.kind === 'reject' && isManager && !readOnly && (
        <ReasonDialog
          title={t('payments.orderPaymentDialogs.rejectPayout')}
          actionLabel={t('payments.orderPaymentDialogs.rejectPayout')}
          help="reject-payout"
          error={error}
          submitting={pending}
          onCancel={() => {
            setReason(null);
            setError(null);
          }}
          onSubmit={async (text) => {
            if (!isManager || readOnly || pending) return;
            setError(null);
            try {
              await reject.mutateAsync({ paymentId: reason.id, reason: text });
              setReason(null);
            } catch (cause) {
              setError(mapPaymentError(cause));
            }
          }}
        />
      )}
    </>
  );
}
